/**
 * Registry of filter-control adapter types.
 *
 * Maps a `data-filter-type` name (e.g. "select", "checkbox") to a FilterControl subclass. A shortcode
 * instance looks up the adapter for each `.kpfsc__filter` here. Themes extend the set via the public
 * PostFilters.registerFilterType() sugar or by filtering the `services` object.
 */
export default class FilterRegistry {
    /** @member {Object<string, Function>} type name → FilterControl subclass. */
    #types = {};

    /** @member {Logger} */
    #logger;

    /**
     * @param {Logger} logger
     */
    constructor(logger) {
        this.#logger = logger;
    }

    /**
     * Registers (or replaces) an adapter type.
     *
     * @param {string} name
     * @param {Function} factory A FilterControl subclass (constructed as `new factory($filter, instance)`).
     * @returns {FilterRegistry}
     */
    register(name, factory) {
        this.#types[name] = factory;
        return this;
    }

    /**
     * @param {string} name
     * @returns {boolean}
     */
    has(name) {
        return Boolean(this.#types[name]);
    }

    /**
     * Instantiates the adapter for a filter. Unknown types fall back to "select" (with a warning) so a
     * stray/misspelled data-filter-type degrades to the default rather than breaking the filter.
     *
     * @param {string} type
     * @param {jQuery} $filter The `.kpfsc__filter` wrapper.
     * @param {ShortcodeInstance} instance
     * @returns {FilterControl|null}
     */
    create(type, $filter, instance) {
        let factory = this.#types[type];

        if(!factory) {
            this.#logger.warn(`Unknown filter type "${type}", falling back to "select".`);
            factory = this.#types["select"];
        }

        if(!factory) {
            return null;
        }

        return new factory($filter, instance);
    }
}
